import React, { useMemo, useState } from 'react';
import { FileText, Loader2, AlertTriangle, Info, FilePlus2 } from 'lucide-react';
import {
  ARKO_CONTRACT_GENERATE_URL,
  getPortalTenantN8nExtras,
  hasPortalAuthzToken,
  portalAuthzHeaders,
  resolveOnlyofficeIssueJwtUrl,
} from '../../constants';
import {
  CONTRACT_TEMPLATES,
  ContractSchema,
  ContractValues,
  SONDERKLAUSEL_LABELS,
  SchemaProp,
  buildInitialFormValues,
  buildSubmitFields,
  getByPath,
  getContractTemplate,
  isConditionalActive,
  isEffectivelyRequired,
  collectLeafFields,
  setByPath,
  validateContractForm,
} from './contractTemplates';

interface ContractGeneratorViewProps {
  customerId: string;
  customerStatus?: string;
  customerEmail?: string;
  /** Antwort des Generate-Endpoints (wie PORTAL_DOCUMENT_UPLOAD) → öffnet den Editor. */
  onDocumentReady: (data: Record<string, unknown>, res: Response) => void;
}

const FIELD_BASE =
  'w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500 transition-colors placeholder-slate-600';

const ContractGeneratorView: React.FC<ContractGeneratorViewProps> = ({
  customerId,
  customerStatus,
  customerEmail,
  onDocumentReady,
}) => {
  const [templateId, setTemplateId] = useState<string>(CONTRACT_TEMPLATES[0]?.id ?? '');
  const template = getContractTemplate(templateId);
  const schema = template?.schema;

  const [form, setForm] = useState<ContractValues>(() =>
    schema ? buildInitialFormValues(schema) : {},
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const leafByPath = useMemo(() => {
    const map: Record<string, ReturnType<typeof collectLeafFields>[number]> = {};
    if (schema) for (const f of collectLeafFields(schema)) map[f.path] = f;
    return map;
  }, [schema]);

  const update = (path: string, value: unknown) => {
    setForm((prev) => setByPath(prev, path, value));
    setErrors((prev) => {
      if (!prev[path]) return prev;
      const next = { ...prev };
      delete next[path];
      return next;
    });
  };

  const onTemplateChange = (id: string) => {
    setTemplateId(id);
    const tpl = getContractTemplate(id);
    setForm(tpl ? buildInitialFormValues(tpl.schema) : {});
    setErrors({});
    setServerError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!schema || !template) return;
    setServerError(null);

    const validation = validateContractForm(schema, form);
    if (!validation.ok) {
      setErrors(validation.errors);
      return;
    }
    setErrors({});

    if (!ARKO_CONTRACT_GENERATE_URL) {
      setServerError('Der Vertragsgenerator ist in diesem Build nicht konfiguriert.');
      return;
    }
    if (!hasPortalAuthzToken()) {
      setServerError('Bitte neu anmelden — für den Vertragsgenerator fehlt ein gültiges Portal-Token.');
      return;
    }

    const fields = buildSubmitFields(schema, form);
    const displayName = buildDisplayName(template.label, form);

    setBusy(true);
    try {
      const url = resolveOnlyofficeIssueJwtUrl(ARKO_CONTRACT_GENERATE_URL);
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...portalAuthzHeaders(url),
        },
        credentials: 'omit',
        body: JSON.stringify({
          templateType: templateId,
          fields,
          customerId,
          display_name: displayName,
          ...(getPortalTenantN8nExtras(customerStatus, customerEmail)),
        }),
      });
      let data: Record<string, unknown> = {};
      try {
        data = (await res.json()) as Record<string, unknown>;
      } catch {
        /* ignore */
      }
      const docUrl = typeof data.document_url === 'string' ? data.document_url.trim() : '';
      if (!res.ok || !docUrl) {
        const errStr =
          typeof data.error === 'string'
            ? data.error
            : !res.ok
              ? `Der Generator antwortete mit Fehler ${res.status}.`
              : 'Antwort ohne document_url — der Generator-Workflow ist evtl. noch nicht aktiv.';
        throw new Error(errStr);
      }
      onDocumentReady(data, res);
    } catch (err) {
      setServerError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  if (!schema || !template) {
    return (
      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 text-slate-400 text-sm">
        Kein Vertragstyp verfügbar.
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in max-w-2xl mx-auto">
      <div>
        <h1 className="text-2xl font-bold text-white">Vertrag erstellen</h1>
        <p className="text-blue-400/80 text-sm font-medium mt-1">Alpha · Entwurfs-Assistent · Keine Rechtsberatung (§ 9 RAO)</p>
        <p className="text-slate-400 mt-1">
          Felder ausfüllen → ARKORBIT erzeugt einen Vertrags-Entwurf (Corporate-Briefkopf, Stub-Klauseln) und öffnet ihn direkt im Editor.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-5">
        {/* Vertragstyp */}
        <div>
          <label className="block text-sm font-bold text-white mb-1.5">Vertragstyp</label>
          <select
            value={templateId}
            onChange={(e) => onTemplateChange(e.target.value)}
            className={FIELD_BASE}
          >
            {CONTRACT_TEMPLATES.map((t) => (
              <option key={t.id} value={t.id}>
                {t.label}
              </option>
            ))}
          </select>
          {template.blurb && <p className="text-slate-500 text-xs mt-1">{template.blurb}</p>}
        </div>

        {/* Dynamische Felder aus dem Schema */}
        {Object.entries(schema.properties).map(([key, prop]) => {
          if (prop.type === 'object' && prop.properties) {
            return (
              <fieldset key={key} className="border border-slate-800 rounded-xl p-4 space-y-3">
                <legend className="px-2 text-sm font-bold text-blue-300">{prop.title || key}</legend>
                {prop.description && <p className="text-slate-500 text-xs -mt-1">{prop.description}</p>}
                {Object.entries(prop.properties).map(([subKey, subProp]) => (
                  <FieldControl
                    key={`${key}.${subKey}`}
                    path={`${key}.${subKey}`}
                    prop={subProp}
                    value={getByPath(form, `${key}.${subKey}`)}
                    required={isPathRequired(schema, form, `${key}.${subKey}`)}
                    error={errors[`${key}.${subKey}`]}
                    onChange={update}
                  />
                ))}
              </fieldset>
            );
          }
          // Bedingte Felder (z. B. befristung_bis) nur zeigen, wenn aktiv.
          const leaf = leafByPath[key];
          if (leaf?.conditional && !isConditionalActive(leaf, form)) return null;
          return (
            <FieldControl
              key={key}
              path={key}
              prop={prop}
              value={getByPath(form, key)}
              required={isPathRequired(schema, form, key)}
              error={errors[key]}
              onChange={update}
            />
          );
        })}

        {serverError && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3 flex items-start gap-2 text-red-300 text-sm">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>{serverError}</span>
          </div>
        )}

        <button
          type="submit"
          disabled={busy}
          className="w-full bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 disabled:cursor-not-allowed text-white font-bold py-3 rounded-xl transition-all flex items-center justify-center gap-2"
        >
          {busy ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" /> Entwurf wird erstellt…
            </>
          ) : (
            <>
              <FilePlus2 className="w-4 h-4" /> Vertrags-Entwurf erstellen
            </>
          )}
        </button>

        <div className="bg-amber-500/5 border border-amber-500/15 rounded-xl p-4 flex items-start gap-3">
          <Info className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
          <p className="text-slate-500 text-xs leading-relaxed">
            Der erzeugte Text ist ein <strong className="text-amber-400/90">Entwurf</strong> mit neutralen Stub-Klauseln
            („[ENTWURF – anwaltlich zu prüfen]") — <strong>keine Rechtsberatung</strong>. Nur fiktive Daten verwenden.
          </p>
        </div>
      </form>

      <div className="flex items-center gap-2 text-slate-600 text-xs">
        <FileText className="w-3.5 h-3.5" />
        <span>Verarbeitung auf dem ARKORBIT-Server (EU). Ausgabe wird in Ihrem Nextcloud abgelegt.</span>
      </div>
    </div>
  );
};

/** Effektive Pflicht (required + if/then) für ein Blatt am Pfad. */
function isPathRequired(schema: ContractSchema, form: ContractValues, path: string): boolean {
  const leaf = collectLeafFields(schema).find((f) => f.path === path);
  return leaf ? isEffectivelyRequired(leaf, form) : false;
}

function buildDisplayName(templateLabel: string, form: ContractValues): string {
  const mieter = getByPath(form, 'mieter.name');
  const who = typeof mieter === 'string' && mieter.trim() ? ` — ${mieter.trim()}` : '';
  const stamp = new Date().toISOString().slice(0, 10);
  return `${templateLabel}${who} (${stamp})`;
}

interface FieldControlProps {
  path: string;
  prop: SchemaProp;
  value: unknown;
  required: boolean;
  error?: string;
  onChange: (path: string, value: unknown) => void;
}

const FieldControl: React.FC<FieldControlProps> = ({ path, prop, value, required, error, onChange }) => {
  const label = (
    <label className="block text-sm font-medium text-slate-200 mb-1">
      {prop.title || path}
      {required && <span className="text-red-400 ml-1">*</span>}
    </label>
  );

  // Boolean → Ja/Nein-Toggle
  if (prop.type === 'boolean') {
    return (
      <div>
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={value === true}
            onChange={(e) => onChange(path, e.target.checked)}
            className="accent-blue-500 w-4 h-4"
          />
          <span className="text-sm text-slate-200">
            {prop.title || path}
            {required && <span className="text-red-400 ml-1">*</span>}
          </span>
        </label>
        {prop.description && <p className="text-slate-500 text-xs mt-1">{prop.description}</p>}
      </div>
    );
  }

  // Array mit Enum-Items → Checkbox-Liste (Sonderklauseln)
  if (prop.type === 'array' && prop.items?.enum) {
    const selected: string[] = Array.isArray(value) ? (value as string[]) : [];
    const toggle = (opt: string) => {
      const next = selected.includes(opt) ? selected.filter((x) => x !== opt) : [...selected, opt];
      onChange(path, next);
    };
    return (
      <div>
        {label}
        {prop.description && <p className="text-slate-500 text-xs mb-2 -mt-0.5">{prop.description}</p>}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {prop.items.enum.map((opt) => (
            <label key={opt} className="flex items-center gap-2 cursor-pointer text-sm text-slate-300">
              <input
                type="checkbox"
                checked={selected.includes(opt)}
                onChange={() => toggle(opt)}
                className="accent-blue-500 w-4 h-4"
              />
              {SONDERKLAUSEL_LABELS[opt] || opt}
            </label>
          ))}
        </div>
        {error && <p className="text-red-400 text-xs mt-1">{error}</p>}
      </div>
    );
  }

  // Enum-String → Select
  if (prop.enum && prop.enum.length) {
    return (
      <div>
        {label}
        <select
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(path, e.target.value)}
          className={`${FIELD_BASE} ${error ? 'border-red-500/60' : ''}`}
        >
          <option value="">Bitte wählen…</option>
          {prop.enum.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
        {prop.description && !error && <p className="text-slate-500 text-xs mt-1">{prop.description}</p>}
        {error && <p className="text-red-400 text-xs mt-1">{error}</p>}
      </div>
    );
  }

  const inputType = prop.format === 'date' ? 'date' : 'text';
  return (
    <div>
      {label}
      <input
        type={inputType}
        inputMode={prop.type === 'number' ? 'decimal' : undefined}
        value={typeof value === 'string' ? value : value == null ? '' : String(value)}
        onChange={(e) => onChange(path, e.target.value)}
        placeholder={prop.type === 'number' ? 'z. B. 1250' : ''}
        className={`${FIELD_BASE} ${error ? 'border-red-500/60' : ''}`}
      />
      {prop.description && !error && <p className="text-slate-500 text-xs mt-1">{prop.description}</p>}
      {error && <p className="text-red-400 text-xs mt-1">{error}</p>}
    </div>
  );
};

export default ContractGeneratorView;
